import classNames from 'classnames'
import keycodes from 'keycodes'
import lodash from 'lodash'
import * as React from 'react'

import {autobind} from '@base/decorators'
import themeable from '@base/react-utils/themeable'

import Icon from '@base-uikit/icons/Icon'

import {createCalendar} from './Calendar'
import {DatePresetsList} from './DatePresetsList'
import * as helpers from './helpers'
import type {
  CalendarSelectState,
  CalendarSelectMode,
  WeekdayHeader,
  CalendarSelectValue,
  CalendarDatePresetType,
  Day,
} from './types'

import css from './CalendarSelect.css'

type CalendarSelectProps = {
  className?: string
  daysOutside?: boolean
  locale?: string
  monthsDisplayed: number
  onKeyDown?: Function
  onValueChange?: Function
  padded?: boolean
  tabIndex?: number
  value?: CalendarSelectValue
  filter?: any
  datePresets?: () => Array<CalendarDatePresetType>
}

// eslint-disable-next-line react/no-unsafe
@themeable(css)
class CalendarSelect extends React.Component<
  Partial<CalendarSelectProps>,
  CalendarSelectState
> {
  ref: HTMLElement | undefined | null
  theme: any

  static defaultProps = {
    daysOutside: true,
    locale: 'en',
    monthsDisplayed: 1,
    padded: false,
    tabIndex: -1,
  }

  constructor(props: Partial<CalendarSelectProps>) {
    super(props)
    this.state = helpers.parseValue(props.value)
  }

  UNSAFE_componentWillMount() {
    this.updateStateWithProps(this.props)
  }

  UNSAFE_componentWillReceiveProps(nextProps: Partial<CalendarSelectProps>) {
    // most of the props are shallow, so this is relatively quick
    if (!lodash.isEqual(this.props, nextProps)) {
      this.updateStateWithProps(nextProps)
    }
  }

  @autobind
  updateStateWithProps(props: Partial<CalendarSelectProps>) {
    const {mode} = helpers.parseFilter(props.filter)

    const newFocus =
      helpers.parseValue(props.value, mode).focus ||
      helpers.nowInBrowserTimezone()
    this.newState(newFocus, mode)
  }

  @autobind
  handleValueChange() {
    const {value, filter, onValueChange} = this.props
    const {focus, mode} = this.state

    const maybeFocus = !focus
      ? helpers.guessDate(lodash.get(filter, 'filter'))
      : focus

    if (onValueChange) {
      const newValue = helpers.formatValue(value, maybeFocus, mode)
      onValueChange(newValue)
    }
  }

  newState(focus: Date, mode: CalendarSelectMode, func?: Function | null) {
    if (
      this.state.focus &&
      lodash.isEqual(focus, this.state.focus) &&
      mode === this.state.mode
    ) {
      return
    }
    this.setState({focus, mode}, () => func && func())
  }

  @autobind
  handleArrowKeyDown(event: any) {
    const keyCode = event.keyCode
    const {mode, focus} = this.state
    const newFocus = helpers.calculateNextDate(focus, keyCode)
    this.newState(newFocus, mode)
  }

  @autobind
  handleFocusNextMonths() {
    const {mode, focus} = this.state
    const newFocus = helpers.addMonths(focus, this.props.monthsDisplayed!)
    this.newState(newFocus, mode)
  }

  @autobind
  handleFocusPrevMonths(event?: any) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    const {mode, focus} = this.state
    const newFocus = helpers.addMonths(focus, -this.props.monthsDisplayed!)
    this.newState(newFocus, mode)
  }

  @autobind
  handleKeyDown(event: any) {
    switch (event.keyCode) {
      case keycodes('pageup'):
        event.preventDefault()
        event.stopPropagation()
        this.handleFocusNextMonths()
        break
      case keycodes('pagedown'):
        event.preventDefault()
        event.stopPropagation()
        this.handleFocusPrevMonths()
        break
      case keycodes('up'):
        event.preventDefault()
        event.stopPropagation()
        this.handleArrowKeyDown(event)
        break
      case keycodes('down'):
        event.preventDefault()
        event.stopPropagation()
        this.handleArrowKeyDown(event)
        break
      case keycodes('enter'):
      case keycodes('space'):
        event.preventDefault()
        event.stopPropagation()
        this.handleValueChange()
        break
      default:
        if (this.props.onKeyDown) {
          this.props.onKeyDown(event)
        }
    }
  }

  getClassName(day: Day) {
    const {daysOutside, value} = this.props
    const {outside, date} = day
    const {focus} = this.state

    if (outside && !daysOutside) {
      return this.theme.hidden
    }

    if (outside) {
      return classNames(this.theme.outside, this.theme.disabled)
    }

    const focused =
      focus && helpers.isSameDay(date, focus) && this.theme.focused

    if (helpers.isSelected(date, value)) {
      return classNames(this.theme.selected, focused)
    }

    if (helpers.isHighlighted(date, value)) {
      return classNames(this.theme.highlighted, focused)
    }

    if (helpers.isToday(date)) {
      return classNames(this.theme.today, focused)
    }

    return classNames(focused)
  }

  renderDay(day: Day) {
    const {daysOutside} = this.props
    const {mode} = this.state
    const {outside, date} = day
    const key = day.toJSON()
    const className = this.getClassName(day)

    return (
      <div
        key={key}
        className={classNames(this.theme.day, className)}
        onClick={(event) =>
          !outside && this.newState(date, mode, this.handleValueChange)
        }
        onKeyDown={this.handleKeyDown}
      >
        {outside && !daysOutside ? '' : day.formatted}
      </div>
    )
  }

  render() {
    const {className, monthsDisplayed, locale} = this.props

    const calendar = createCalendar(
      helpers.startOfMonth(
        this.state.focus ||
          lodash.get(this.props, lodash.get(this.props, ['filter', 'mode'])) ||
          lodash.get(this.props, 'start')
      ),
      monthsDisplayed,
      // @ts-expect-error locale shouldn't be here, however I don't want to remove it
      locale
    )

    return (
      <div
        ref={(r) => {
          this.ref = r
        }}
        className={classNames(this.theme.calendar, className)}
        onKeyDown={this.handleKeyDown}
        tabIndex={this.props.tabIndex}
      >
        {this.props.datePresets != null && (
          <DatePresetsList
            className={this.theme.datePresets}
            datePresets={this.props.datePresets}
            value={this.props.value}
            onValueChange={this.props.onValueChange}
          />
        )}
        <div className={this.theme.months}>
          {calendar.months.map((month, monthIndex) => {
            const prevClassName = classNames(
              this.theme.monthArrowPrev,
              !month.hasArrowPrev && this.theme.hidden
            )
            const nextClassName = classNames(
              this.theme.monthArrowNext,
              !month.hasArrowNext && this.theme.hidden
            )

            return (
              <div
                className={this.theme.month}
                key={monthIndex}
                tabIndex={this.props.tabIndex}
              >
                <div className={this.theme.monthsNavigation}>
                  <Icon
                    className={prevClassName}
                    onClick={this.handleFocusPrevMonths}
                    name="arrow-left"
                  />

                  <div className={this.theme.monthTitle}>{month.title}</div>

                  <Icon
                    className={nextClassName}
                    onClick={this.handleFocusNextMonths}
                    name="arrow-right"
                  />
                </div>

                <div className={this.theme.weekdayHeaders}>
                  {calendar.weekdayHeaders.map(
                    (header: WeekdayHeader, headerIndex: number) => (
                      <div
                        key={headerIndex}
                        className={this.theme.weekdayHeader}
                      >
                        <span title={header.title}>{header.content}</span>
                      </div>
                    )
                  )}
                </div>

                <div className={this.theme.days}>
                  {month.weeks.map((week: Array<Day>, weekIndex: number) => (
                    <div key={weekIndex} className={this.theme.week}>
                      {week.map((day: Day) => this.renderDay(day))}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}

export default CalendarSelect
